import { ViteSSG } from 'vite-ssg'
import { createPinia } from 'pinia'
import App from './App.vue'
import { createSSRRouter, isClient } from './router'

// Import global styles
// Tailwind's standard reset (border-box, border-style: solid, margin reset),
// the base @elveris/ui components are written against — same as elveris-avm.
// Under Tailwind this app ran with `preflight: false`, so every element was
// content-box (a `w-full p-4` kit Alert overflowed its card by 32 px) and
// `border` utilities drew nothing (border-style stayed `none`).
import '@unocss/reset/tailwind.css'
// @elveris/theme base tokens (motion: --duration-*, --ease-*; html.dark
// overrides; focus-visible ring). The kit's components reference them — kit
// <Button> is `duration-[var(--duration-base)] ease-[var(--ease-out)]` — and
// without this file those variables were undefined here, so every kit Button
// transition computed to 0s / ease. Placed BEFORE virtual:uno.css (as in
// elveris-website): its tokens use `:root:root` and win by specificity in any
// order, while its few class-level rules (.theme-transitioning *, …) then lose
// ties to utilities instead of overriding them. Relative path as in
// elveris-avm (the theme package's `exports` map does not expose ./styles/*).
import '../../../elveris-ui/packages/features/theme/src/styles/theme-base.css'
import 'virtual:uno.css'
import './styles/index.css'

// Environment types (vite-env.d.ts) reach the compiler through tsconfig
// `include` + `types: ["vite/client"]`. They used to be "imported" here as a
// side effect (`import './vite-env.d.ts'`) — a declaration file has no runtime
// module, and TypeScript 6 rejects it (TS2882).

// Browser error relay to the dev server's terminal. Its endpoint
// (/__runtime-error) is a Vite dev-server middleware (vite.config.ts), so the
// relay is loaded in development only; in the built app every report used to
// end in a 404 and a console error of its own. (2026-09-30)
if (import.meta.env.DEV && typeof window !== 'undefined') {
  void import('./utils/runtime-error-handler')
}

// Create the ViteSSG app with proper router configuration
export const createApp = ViteSSG(
  App,
  {
    routes: createSSRRouter().getRoutes(),
    base: import.meta.env.BASE_URL,
  },
  (ctx) => {
    // Install plugins
    const { app, router } = ctx
    app.use(createPinia())

    // Simplified client-side initialization
    if (isClient()) {
      // Ensure proper client-side routing
      if (router && typeof router.replace === 'function') {
        // Fix any initial routing issues
        const currentPath = window.location.pathname
        if (router.currentRoute.value.path !== currentPath) {
          router.replace(currentPath).catch(() => {
            // Ignore navigation errors during initialization
          })
        }
      }

      // Minimal metadata updates
      router.afterEach((to) => {
        if (to.meta.title) {
          document.title = to.meta.title as string
        }
      })

      // Removed 2026-10-01 (archived in _archive/kit-migration-2026-09-30/):
      // - a global security-store init + 5 s polling on EVERY page, duplicating
      //   what SecurityDashboard.vue does for itself; its "cleanup" called
      //   onUnmounted outside a component, which does nothing;
      // - feature detection whose result was provide()d after mount and never
      //   inject()ed anywhere;
      // - "initialized (fallback mode)" log lines printed after a failure;
      // - a second service-worker registration that swallowed errors; the
      //   PWA plugin already injects registerSW.js.

      // The app-ready signal (loading screen, tests) is set in App.vue onMounted.
    }
  }
)